import { Params } from '@angular/router';
import { RmaIcons } from '@rma/generic/ui/icon';
import type { TypeGuard } from '@rma/generic/util/pipes/type-guard';

export type NavigationGroupItem = NavigationItem | NavigationMenu;
export type NavigationGroupTitle = 'collect' | 'share' | 'monitor' | 'manage';

export interface NavigationGroup {
  title?: NavigationGroupTitle;
  items: NavigationGroupItem[];
}

export interface NavigationGooglePermissions {
  hasConnectedGmb: boolean;
  hasGoogleConnect: boolean;
  hasOwnGoogleAskForReviews: boolean;
  hasGoogleAskForReviews: boolean;
}

export interface NavigationItem extends NavigationSubItem {
  type: 'item';
}

export interface NavigationMenu {
  type: 'menu';
  icon: RmaIcons;
  label: string;
  children: NavigationSubItem[];
  pathsInArea?: string[];
  disabled?: boolean;
}

export interface NavigationSubItem {
  icon?: RmaIcons;
  label: string;
  link: string;
  disabled?: boolean;
  isBeta?: boolean;
  pathsInArea?: string[];
  queryParams?: Params;
  target?: string;
}

export const isMenu: TypeGuard<NavigationGroupItem, NavigationMenu> = (shape: NavigationGroupItem): shape is NavigationMenu =>
  shape.type === 'menu';

export const isItem: TypeGuard<NavigationGroupItem, NavigationItem> = (shape: NavigationGroupItem): shape is NavigationItem =>
  shape.type === 'item';

export function createNavigationGroup(items: NavigationGroupItem[], title?: NavigationGroupTitle): NavigationGroup {
  return {
    title,
    items,
  };
}

export function createBetaNavigationItem(
  label: string,
  link: string,
  icon?: RmaIcons,
  pathsInArea?: string[],
  target?: string,
): NavigationItem {
  return {
    isBeta: true,
    ...createNavigationItem(label, link, icon, pathsInArea, target),
  };
}

export function createNavigationItem(
  label: string,
  link: string,
  icon?: RmaIcons,
  pathsInArea?: string[],
  target?: string,
): NavigationItem {
  return {
    type: 'item',
    ...createSubNavigationItem(label, link, icon, pathsInArea, target),
  };
}

export function createNavigationMenu(label: string, icon: RmaIcons, children: NavigationSubItem[], pathsInArea?: string[]): NavigationMenu {
  return {
    type: 'menu',
    label,
    icon,
    children,
    pathsInArea,
  };
}

export function createNavigationSubItem(label: string, link: string, queryParams?: Params, pathsInArea?: string[]): NavigationSubItem {
  return createSubNavigationItem(label, link, undefined, pathsInArea, undefined, queryParams);
}

function createSubNavigationItem(
  label: string,
  link: string,
  icon?: RmaIcons,
  pathsInArea?: string[],
  target?: string,
  queryParams?: Params,
): NavigationSubItem {
  return {
    label,
    link,
    icon,
    pathsInArea,
    target,
    queryParams,
  };
}
